import { useCallback, useEffect, useState } from "react";
import { oidcAuthClient, PopupBlockedError } from "@contexts/OidcAuthClient";
import { isEmbedded } from "@/embed/embedHost";

interface UseFullLayoutAuthGateViewModelArgs {
  authLoading: boolean;
  globalLoading: boolean;
  isAuthenticated: boolean;
  accessToken: string | null;
  regionBaseUrl: string | null;
  availableRegions: unknown[];
}

interface FullLayoutAuthGateViewModel {
  shouldNavigateToRegionSelection: boolean;
  isRedirectingToLogin: boolean;
  loginRedirectFailed: boolean;
  /**
   * The user has to start sign-in themselves. Inside an embedded host the flow
   * is a popup, and browsers only allow those from a user gesture, so the view
   * offers a button rather than starting anything on its own.
   */
  requiresManualSignIn: boolean;
  /**
   * The browser refused the sign-in popup. Separate from a failed sign-in
   * because the user can clear it themselves, and the view tells them how.
   */
  popupBlocked: boolean;
  triggerLoginRedirect: () => Promise<void>;
}

export const useFullLayoutAuthGateViewModel = ({
  authLoading,
  globalLoading,
  isAuthenticated,
  accessToken,
  regionBaseUrl,
  availableRegions,
}: UseFullLayoutAuthGateViewModelArgs): FullLayoutAuthGateViewModel => {
  const [isRedirectingToLogin, setIsRedirectingToLogin] = useState(false);
  const [loginRedirectFailed, setLoginRedirectFailed] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);

  const shouldNavigateToRegionSelection =
    !isAuthenticated &&
    !!accessToken &&
    !regionBaseUrl &&
    availableRegions.length > 1;

  const requiresManualSignIn =
    isEmbedded() &&
    !isAuthenticated &&
    !authLoading &&
    !globalLoading &&
    !shouldNavigateToRegionSelection;

  const triggerLoginRedirect = useCallback(async () => {
    if (isRedirectingToLogin) {
      return;
    }

    setIsRedirectingToLogin(true);
    setLoginRedirectFailed(false);
    setPopupBlocked(false);

    try {
      await oidcAuthClient.startInteractiveSignIn();
    } catch (error) {
      // A blocked popup is the user's to fix, not a fault to report: the view
      // says how to allow it and offers the button again.
      if (error instanceof PopupBlockedError) {
        setPopupBlocked(true);
        setIsRedirectingToLogin(false);
        return;
      }

      console.error(
        "[Region Auth] Interactive sign-in redirect failed:",
        error,
      );
      setLoginRedirectFailed(true);
      setIsRedirectingToLogin(false);
    }
  }, [isRedirectingToLogin]);

  // Auto-trigger the interactive sign-in redirect once we know the user
  // definitely isn't authenticated and isn't already mid-flow (region
  // selection, an auth callback route, or a prior in-flight redirect).
  useEffect(() => {
    if (
      authLoading ||
      globalLoading ||
      isAuthenticated ||
      shouldNavigateToRegionSelection ||
      loginRedirectFailed
    ) {
      return;
    }

    if (oidcAuthClient.isAuthFlowRoute()) {
      return;
    }

    // Inside a host, sign-in opens a popup, and a popup opened from an effect
    // is blocked by the browser. Leave it to the button the view renders.
    if (isEmbedded()) {
      return;
    }

    void triggerLoginRedirect();
  }, [
    authLoading,
    globalLoading,
    isAuthenticated,
    shouldNavigateToRegionSelection,
    loginRedirectFailed,
    triggerLoginRedirect,
  ]);

  return {
    shouldNavigateToRegionSelection,
    isRedirectingToLogin,
    loginRedirectFailed,
    requiresManualSignIn,
    popupBlocked,
    triggerLoginRedirect,
  };
};
